import { useContext, useEffect, useState, useMemo, useRef } from 'react';
import Button from '@components/DesignLibrary/Button';
import { IBM_Plex_Mono as ibmPlexMono } from 'next/font/google';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import ProgressIndicator from '@/components/ProgressIndicator';
import Spacer from '@/components/Spacer';
import { useIsMobile } from '@/hooks';
import { SIZES } from '@/styles/media';
import { useUser } from '@/providers/Auth';
import { DocumentAccessContext } from '@/providers/DocumentAccess';
import preloadDocument from '@/helpers/preloadDocument';
import { logError } from '@providers/ErrorTracking';
import * as ApiModels from '@typings/api-models';
import { FixedSizeList as List } from 'react-window';
import DocumentPreview from '@/components/Document/DocumentPreview/Content';
import MimeTypes from '@/typings/MimeTypes';
import BannerStatus from '@/components/DesignLibrary/BannerStatus';
import Tabs from '@/components/DesignLibrary/Tabs';
import CopyButton from '@/components/CopyButton';

import s from './CreditReportDetails.module.scss';

type Props = {
  creditReportResponses: ApiModels.CreditReportResponse[];
  tab: string;
};

const mono = ibmPlexMono({
  subsets: ['latin'],
  weight: '400',
});

const CreditReportResponse: React.FC<Props> = ({ creditReportResponses, tab }) => {
  const [user] = useUser();
  const { accessToken } = useContext(DocumentAccessContext);
  const [doc, setDoc] = useState<'credit-report' | 'previous-bankruptcies' | 'property-data'>('credit-report');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previousBankruptciesPreviewUrl, setPreviousBankruptciesPreviewUrl] = useState<string | null>(null);
  const [propertyDataPreviewUrl, setPropertyDataPreviewUrl] = useState<string | null>(null);
  const [fetchingPdf, setFetchingPdf] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number>(0); // Type the state as number
  const [creditReportResponse, setCreditReportResponse] = useState<ApiModels.CreditReportResponse | null>(null);

  useEffect(() => {
    if (tab) {
      setCreditReportResponse(creditReportResponses.find(({ slug }) => slug === tab) || null);
    }
  }, [tab, creditReportResponses]);

  const isMobile = useIsMobile(SIZES.md);

  useEffect(() => {
    const loadPdf = async (): Promise<void> => {
      setFetchingPdf(true);

      if (creditReportResponse?.pdfRequestId && (accessToken || user?.id)) {
        preloadDocument({
          accessToken,
          id: creditReportResponse.pdfRequestId,
        })
          .then(blob => {
            const typeBlob = new Blob([blob], { type: 'application/pdf' });
            const blobPreviewUrl = window.URL.createObjectURL(typeBlob);

            setPreviewUrl(blobPreviewUrl);
          })
          .catch(error => {
            logError(error);
          })
          .finally(() => {
            setFetchingPdf(false);
          });
      } else {
        setPreviewUrl(null);
      }

      if (creditReportResponse?.previousBankruptciesDocId && (accessToken || user?.id)) {
        preloadDocument({
          accessToken,
          id: creditReportResponse.previousBankruptciesDocId,
        })
          .then(blob => {
            const typeBlob = new Blob([blob], { type: 'application/pdf' });
            const blobPreviewUrl = window.URL.createObjectURL(typeBlob);

            setPreviousBankruptciesPreviewUrl(blobPreviewUrl);
          })
          .catch(error => {
            logError(error);
          });
      } else {
        setPreviousBankruptciesPreviewUrl(null);
      }

      if (creditReportResponse?.propertyDataDocId && (accessToken || user?.id)) {
        preloadDocument({
          accessToken,
          id: creditReportResponse.propertyDataDocId,
        })
          .then(blob => {
            const typeBlob = new Blob([blob], { type: 'application/pdf' });
            const blobPreviewUrl = window.URL.createObjectURL(typeBlob);

            setPropertyDataPreviewUrl(blobPreviewUrl);
          })
          .catch(error => {
            logError(error);
          });
      } else {
        setPropertyDataPreviewUrl(null);
      }
    };

    loadPdf();
  }, [
    creditReportResponse?.pdfRequestId,
    creditReportResponse?.previousBankruptciesDocId,
    creditReportResponse?.propertyDataDocId,
    accessToken,
    user?.id,
  ]);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.clientHeight);
    }

    const handleResize = (): void => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.clientHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [creditReportResponse?.response]);

  const downloadPdf = (): void => {
    const downloadBlob = (url: string, fileName: string): void => {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    if (previewUrl && doc === 'credit-report') {
      downloadBlob(previewUrl, 'Credit Report.pdf');
    }

    if (previousBankruptciesPreviewUrl && doc === 'previous-bankruptcies') {
      downloadBlob(previousBankruptciesPreviewUrl, 'Previous Bankruptcies.pdf');
    }

    if (propertyDataPreviewUrl && doc === 'property-data') {
      downloadBlob(propertyDataPreviewUrl, 'Property Ownership.pdf');
    }
  };

  const jsonData = creditReportResponse?.response ? JSON.stringify(creditReportResponse?.response, null, 2) : '';

  const previousBankruptciesRes = creditReportResponse?.previousBankruptciesResponse;

  const previousBankruptciesJsonData = previousBankruptciesRes ? JSON.stringify(previousBankruptciesRes, null, 2) : '';

  const propertyDataRes = creditReportResponse?.propertyDataResponse;

  const propertyJsonData = propertyDataRes ? JSON.stringify(propertyDataRes, null, 2) : '';

  const combinedJsonLines = useMemo(() => {
    const jsonLines = jsonData ? jsonData.split('\n') : [];

    if (previousBankruptciesRes && Object.keys(previousBankruptciesRes).length > 0) {
      jsonLines.push(...previousBankruptciesJsonData.split('\n'));
    }

    if (propertyDataRes && Object.keys(propertyDataRes).length > 0) {
      jsonLines.push(...propertyJsonData.split('\n'));
    }

    return jsonLines;
  }, [jsonData, previousBankruptciesJsonData, propertyJsonData]);

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }): JSX.Element => (
    <div style={style}>
      <SyntaxHighlighter
        language="json"
        customStyle={{
          backgroundColor: 'var(--color-gray-0)',
          fontSize: 12,
          lineHeight: 0,
          margin: 0,
          overflow: 'hidden',
          padding: '8px 16px',
        }}
        codeTagProps={{ style: mono.style }}
      >
        {combinedJsonLines[index]}
      </SyntaxHighlighter>
    </div>
  );

  return (
    <>
      {creditReportResponse?.response && Object.keys(creditReportResponse.response).length > 0
        ? (
          <div className={isMobile ? s.contentWrapperMobile : s.contentWrapper}>
            <div className={s.content}>
              {(previousBankruptciesPreviewUrl || propertyDataPreviewUrl) && (
                <Tabs
                  inputName="pdfs"
                  inputs={[
                    {
                      id: 'credit-report',
                      label: 'Credit Report',
                    },
                    ...(previousBankruptciesPreviewUrl
                      ? [
                        {
                          id: 'previous-bankruptcies',
                          label: 'Previous Bankruptcies',
                        },
                      ]
                      : []),
                    ...(propertyDataPreviewUrl
                      ? [
                        {
                          id: 'property-data',
                          label: 'Property Data',
                        },
                      ]
                      : []),
                  ]}
                  checked={doc}
                  onChange={t => setDoc(t as 'credit-report' | 'previous-bankruptcies' | 'property-data')}
                />
              )}
              <div className={s.padding}>
                <div className={s.documentContainer}>
                  <Button variant="secondary" size="sm" onClick={downloadPdf}>
                    Download
                  </Button>
                  {!fetchingPdf && previewUrl && doc === 'credit-report' && <DocumentPreview url={previewUrl} mimeType={MimeTypes.PDF} />}
                  {!fetchingPdf && !previewUrl && <p className="body-sm">No PDF available</p>}
                  {!fetchingPdf && previousBankruptciesPreviewUrl && doc === 'previous-bankruptcies' && (
                    <DocumentPreview url={previousBankruptciesPreviewUrl} mimeType={MimeTypes.PDF} />
                  )}
                  {!fetchingPdf && propertyDataPreviewUrl && doc === 'property-data' && (
                    <DocumentPreview url={propertyDataPreviewUrl} mimeType={MimeTypes.PDF} />
                  )}
                  {fetchingPdf && (
                    <>
                      <Spacer />
                      <ProgressIndicator isCentered />
                    </>
                  )}
                </div>
              </div>
            </div>
            {!isMobile && (
              <div className={s.content} ref={contentRef}>
                {creditReportResponse.response
                  ? (
                    <div style={{ position: 'relative' }}>
                      <CopyButton text={jsonData} style={{ position: 'absolute', right: 'var(--spacer-sm)', top: 'var(--spacer-sm)' }}>
                        Copy
                      </CopyButton>
                      <List height={contentHeight} itemCount={combinedJsonLines.length} itemSize={16} width="100%">
                        {Row}
                      </List>
                    </div>
                  )
                  : (
                    <>
                      <Spacer />
                      <ProgressIndicator isCentered />
                      <Spacer />
                    </>
                  )}
              </div>
            )}
          </div>
        )
        : (
          <div style={{ padding: 16 }}>
            <BannerStatus status="warning" label="Your client has not yet approved the retrieval of their credit report." />
          </div>
        )}
    </>
  );
};

export default CreditReportResponse;
